import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command';
import { Eye, MapPin, Download, Loader2, FileSpreadsheet, FileText, Layers, Landmark, Trash2, CreditCard, Truck, Search, RotateCcw, Filter, ChevronsUpDown, Check, X, Wallet, Columns3, Package, MessageCircle, Mail, Pencil } from 'lucide-react';
import { adminAPI, collectionPointsAPI, ordersAPI } from '@/lib/api';
import { usePagination, TablePagination } from '@/components/admin/TablePagination';
import { toast } from '@/components/ui/sonner';
import { statusLabel, statusColor, normalizeStatus, PIPELINE_STATUSES, BULK_TARGETS, BULK_DESTRUCTIVE, PAYMENT_OPTIONS, paymentLabel, paymentColor } from '@/lib/orderStatus';
import { formatUnitSize } from '@/lib/units';
import { money } from '@/lib/format';

// Statuses offered in the top filter dropdown.
const statusOptions = PIPELINE_STATUSES;

// Store-credit settlement info for an order (safe on legacy orders).
const creditInfo = (order) => {
  const creditApplied = Math.round((Number(order?.credit_applied) || 0) * 100) / 100;
  const total = Math.round((Number(order?.total) || 0) * 100) / 100;
  const ap = order?.amount_payable;
  const amountPayable = Math.round((ap == null ? total : Number(ap) || 0) * 100) / 100;
  const paid = (order?.payment_status || 'unpaid') === 'paid';
  return {
    creditApplied,
    amountPayable,
    hasCredit: creditApplied > 0,
    fullyCovered: creditApplied > 0 && amountPayable <= 0,
    partialUnpaid: creditApplied > 0 && amountPayable > 0 && !paid,
  };
};

// Columns the admin can show/hide (order-id, customer, actions and the row
// checkbox are always visible). Persisted per-admin in localStorage.
const TOGGLE_COLUMNS = [
  { key: 'supplier', label: 'Supplier' },
  { key: 'collection', label: 'Collection' },
  { key: 'items', label: 'Items' },
  { key: 'stage', label: 'Order Stage' },
  { key: 'payment', label: 'Payment' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'total', label: 'Total' },
  { key: 'credit', label: 'Credit' },
  { key: 'cash', label: 'Cash Payable' },
  { key: 'date', label: 'Date' },
];
const COLUMN_PREF_KEY = 'avz_orders_hidden_cols';

// Stages hidden on first load (user can re-add them via the multi-select).
const DEFAULT_HIDDEN_STAGES = ['closed', 'cancelled', 'rejected', 'expired'];

// Which values to EXCLUDE server-side. Empty selection OR all selected = no filter.
const excludeOf = (options, selected) => {
  if (!selected.length || selected.length === options.length) return [];
  return options.filter((o) => !selected.includes(o));
};

// Reusable checkbox multi-select in a popover (Stage / Payment / Period filters).
function MultiSelectFilter({ testid, icon: Icon, allLabel, options, selected, onChange, renderOption, width = 'sm:w-44', disabled = false }) {
  const allSelected = selected.length === 0 || selected.length === options.length;
  const label = allSelected
    ? allLabel
    : selected.length === 1
      ? renderOption(selected[0])
      : `${selected.length} selected`;
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          role="combobox"
          disabled={disabled}
          data-testid={testid}
          className={`w-full ${width} justify-between rounded-xl bg-card border-border font-body font-normal text-sm disabled:opacity-50`}
        >
          <span className="flex items-center gap-2 min-w-0">
            {Icon && <Icon className="h-3.5 w-3.5 text-teal shrink-0" />}
            <span className="truncate">{label}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[13rem] p-1 rounded-xl" align="start">
        <button
          type="button"
          onClick={() => onChange([...options])}
          data-testid={`${testid}-all`}
          className="w-full flex items-center px-2 py-1.5 rounded-lg hover:bg-muted font-body text-sm"
        >
          <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded border ${allSelected ? 'bg-teal border-teal' : 'border-border'}`}>
            {allSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
          </div>
          {allLabel}
        </button>
        {options.map((opt) => {
          const checked = !allSelected && selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              data-testid={`${testid}-option-${opt}`}
              className="w-full flex items-center px-2 py-1.5 rounded-lg hover:bg-muted font-body text-sm"
            >
              <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded border ${checked ? 'bg-teal border-teal' : 'border-border'}`}>
                {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
              </div>
              {renderOption(opt)}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

const NOTIF_EVENT_LABELS = {
  confirmed: 'Order confirmed',
  packing: 'Packing',
  ready_for_collection: 'Ready for collection',
  closed: 'Order collected',
  rejected: 'Order rejected',
  cancelled: 'Order cancelled',
  payment_requested: 'Payment requested',
  payment_received: 'Payment received',
  invoice: 'Invoice',
  moq_not_met: 'MOQ not met',
  cart_reminder: 'Cart reminder',
  window_opening: 'Window opening',
};
const notifEventLabel = (e) =>
  NOTIF_EVENT_LABELS[e] || (e || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Collection-WhatsApp status derived from an order's notification_log.
const waCollectionStatus = (order) => {
  const logs = (order.notification_log || []).filter(
    (n) => n.channel === 'whatsapp' && n.event === 'ready_for_collection'
  );
  const wa = logs[logs.length - 1];
  const inCollection = ['ready_for_collection', 'collection_notification_sent'].includes(order.status);
  if (!wa) {
    return inCollection
      ? { state: 'pending', label: 'Not sent', cls: 'text-amber-300 border-amber-400/40 bg-amber-400/10' }
      : { state: 'na', label: '—', cls: 'text-foreground/30 border-transparent' };
  }
  if (wa.read_at) return { state: 'read', label: 'Read', cls: 'text-teal border-teal/60 bg-teal/15' };
  if (wa.delivery_status === 'delivered' || wa.delivered_at) return { state: 'delivered', label: 'Delivered', cls: 'text-teal border-teal/40 bg-teal/10' };
  if (!wa.success || wa.delivery_status === 'failed') return { state: 'failed', label: 'Failed', cls: 'text-red-400 border-red-500/40 bg-red-500/10' };
  return { state: 'sent', label: 'Sent', cls: 'text-foreground/70 border-border bg-muted' };
};

export default function AdminOrders() {
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get('status')
      ? [searchParams.get('status')]
      : PIPELINE_STATUSES.filter((s) => !DEFAULT_HIDDEN_STAGES.includes(s))
  );
  const [paymentFilter, setPaymentFilter] = useState(
    searchParams.get('payment') ? [searchParams.get('payment')] : []
  );
  const [selectedSuppliers, setSelectedSuppliers] = useState([]); // [] = all
  const [products, setProducts] = useState([]);
  const [productFilter, setProductFilter] = useState(null); // {id,name,sku} or null
  const [filterCollectionPoint, setFilterCollectionPoint] = useState('all');
  const [collectionPoints, setCollectionPoints] = useState([]);
  const [exportingOrders, setExportingOrders] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [monthFilter, setMonthFilter] = useState([]); // [] = all periods
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [custCredit, setCustCredit] = useState(null); // customer's current store-credit balance
  const [hiddenCols, setHiddenCols] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(COLUMN_PREF_KEY) || '[]')); }
    catch { return new Set(); }
  });
  const colVisible = (key) => !hiddenCols.has(key);
  // Minimum pixel widths per column so the table keeps its shape and the
  // wrapper scrolls horizontally instead of squashing cells (esp. the fixed
  // stage/payment dropdowns). Adapts as columns are hidden.
  const COL_MIN_W = { supplier: 140, collection: 140, items: 120, stage: 190, payment: 190, whatsapp: 130, total: 110, credit: 120, cash: 130, date: 100 };
  const tableMinWidth = 470 /* checkbox 48 + order id 170 + customer 182 + actions ~70 */
    + TOGGLE_COLUMNS.reduce((s, c) => s + (colVisible(c.key) ? (COL_MIN_W[c.key] || 0) : 0), 0);
  const toggleColumn = (key) => {
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify([...next]));
      return next;
    });
  };
  const [updating, setUpdating] = useState(false);
  const [payingId, setPayingId] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [exportingAgg, setExportingAgg] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTarget, setBulkTarget] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [includeNotified, setIncludeNotified] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [rowResendId, setRowResendId] = useState(null);
  const [waFailedOnly, setWaFailedOnly] = useState(false);
  const [refundItems, setRefundItems] = useState([]); // items shown in the refund modal
  const [refundQty, setRefundQty] = useState({}); // product_id -> units to refund
  const [refundSelIds, setRefundSelIds] = useState(new Set()); // checkbox selection in the items list
  const [refundDest, setRefundDest] = useState('credit');
  const [refundNote, setRefundNote] = useState('');
  const [refundBusy, setRefundBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Manual "send any WhatsApp template" (Part B)
  const [waTemplates, setWaTemplates] = useState([]);
  const [waTemplatesTo, setWaTemplatesTo] = useState('');
  const [waTemplatesLoading, setWaTemplatesLoading] = useState(false);
  const [waTemplateChoice, setWaTemplateChoice] = useState('');
  const [waSending, setWaSending] = useState(false);

  const autoOpenedRef = useRef(false);

  // Client-side search (customer / order number) + financial-period (month) filter.
  const monthOptions = [...new Set(orders.map(o => (o.created_at || '').slice(0, 7)).filter(Boolean))].sort().reverse();
  const q = searchText.trim().toLowerCase();
  const filteredOrders = orders.filter(o => {
    if (monthFilter.length && !monthFilter.includes((o.created_at || '').slice(0, 7))) return false;
    if (!q) return true;
    const num = (o.order_number || '').toLowerCase();
    const name = (o.contact_name || o.user_name || '').toLowerCase();
    const email = (o.contact_email || '').toLowerCase();
    return num.includes(q) || name.includes(q) || email.includes(q);
  });
  const ordersPg = usePagination(filteredOrders, 15);
  const pageOrders = ordersPg.pageItems;
  const monthLabelShort = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    return new Date(Number(y), Number(mo) - 1, 1).toLocaleString('en-ZA', { month: 'short', year: 'numeric' });
  };

  const fetchOrders = () => {
    const params = {};
    const exStatus = excludeOf(statusOptions, statusFilter);
    if (exStatus.length) params.exclude_status = exStatus.join(',');
    const exPayment = excludeOf(PAYMENT_OPTIONS, paymentFilter);
    if (exPayment.length) params.exclude_payment = exPayment.join(',');
    if (selectedSuppliers.length) params.supplier_ids = selectedSuppliers.join(',');
    if (productFilter) params.product_id = productFilter.id;
    if (waFailedOnly) params.wa_status = 'failed';
    if (filterCollectionPoint && filterCollectionPoint !== 'all') params.collection_point_id = filterCollectionPoint;
    adminAPI.listOrders(params)
      .then(r => {
        setOrders(r.data);
        // Deep-link from the dashboard/emails: ?order=<id> opens that order once,
        // even if it's filtered out of the current list (e.g. a closed/cancelled stage).
        if (!autoOpenedRef.current) {
          const oid = searchParams.get('order');
          if (oid) {
            const found = (r.data || []).find(o => o.id === oid);
            if (found) {
              setSelectedOrder(found);
            } else {
              ordersAPI.get(oid).then(res => { if (res.data) setSelectedOrder(res.data); }).catch(() => {});
            }
          }
          autoOpenedRef.current = true;
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchOrders(); }, [statusFilter, paymentFilter, selectedSuppliers, filterCollectionPoint, productFilter, waFailedOnly]);
  // Inline setState keeps the React-compiler lint scoped to this effect.
  useEffect(() => { adminAPI.listSuppliers().then(r => setSuppliers(r.data)).catch(() => {}); }, []);
  useEffect(() => { adminAPI.listProducts().then(r => setProducts(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { collectionPointsAPI.listAll().then(r => setCollectionPoints(r.data || [])).catch(() => {}); }, []);

  // Load the customer's current store-credit balance when a detail dialog opens.
  useEffect(() => {
    let active = true;
    setCustCredit(null);
    const uid = selectedOrder?.user_id;
    if (!uid) return;
    adminAPI.creditTransactions(uid)
      .then(r => { if (active) setCustCredit(typeof r.data?.balance === 'number' ? r.data.balance : null); })
      .catch(() => {});
    return () => { active = false; };
  }, [selectedOrder?.id, selectedOrder?.user_id]);

  const updateStatus = async (orderId, newStatus) => {
    const order = orders.find(o => o.id === orderId) || selectedOrder;
    // Soft warning: confirming/progressing an unpaid Type B order.
    const advancing = ['confirmed', 'packing', 'ready_for_collection'].includes(newStatus);
    const isTypeB = order?.payment_workflow === 'after_confirmation';
    const unpaid = (order?.payment_status || 'unpaid') !== 'paid';
    if (advancing && isTypeB && unpaid) {
      if (!window.confirm(`This order isn't paid yet. Move it to "${statusLabel(newStatus)}" anyway?`)) return;
    }
    setUpdating(true);
    try {
      await adminAPI.updateOrder(orderId, { status: newStatus });
      toast.success(`Order stage updated to ${statusLabel(newStatus)}`);
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch {
      toast.error('Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  const reinstateOrder = async (orderId) => {
    if (!window.confirm('Reinstate this order? It will be restored to its previous stage and excluded from auto-expiry.')) return;
    setUpdating(true);
    try {
      const res = await adminAPI.reinstateOrder(orderId);
      toast.success('Order reinstated');
      if (selectedOrder?.id === orderId) setSelectedOrder(res.data || null);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reinstate');
    } finally {
      setUpdating(false);
    }
  };

  const updatePayment = async (orderId, newPayment) => {
    setPayingId(orderId);
    try {
      const res = await adminAPI.setPaymentStatus(orderId, newPayment);
      toast.success(`Payment set to ${paymentLabel(newPayment)}`);
      if (selectedOrder?.id === orderId) setSelectedOrder(res.data || null);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update payment');
    } finally {
      setPayingId(null);
    }
  };

  const toggleSupplier = (id) => {
    setSelectedSuppliers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleRow = (id) => {    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds(prev => {
      if (prev.size === filteredOrders.length) return new Set();
      return new Set(filteredOrders.map(o => o.id));
    });
  };
  const resendWhatsAppRow = async (order) => {
    const logs = (order.notification_log || []).filter((n) => n.channel === 'whatsapp' && n.event === 'ready_for_collection');
    if (logs.some((n) => n.success) && !window.confirm('This customer was already notified on WhatsApp. Resend anyway?')) return;
    setRowResendId(order.id);
    try {
      const res = await adminAPI.resendWhatsApp(order.id);
      if (res.data?.success) toast.success('WhatsApp resent'); else toast.error(res.data?.detail || 'WhatsApp failed to send');
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend WhatsApp');
    } finally {
      setRowResendId(null);
    }
  };

  const saveContactPhone = async () => {
    setSavingPhone(true);
    try {
      const res = await adminAPI.updateOrderContactPhone(selectedOrder.id, phoneInput);
      setSelectedOrder(res.data);
      setEditingPhone(false);
      toast.success(`Contact number updated to ${res.data.contact_phone}`);
      fetchOrders();
      if (['ready_for_collection', 'collection_notification_sent'].includes(res.data.status)
          && window.confirm('Number updated. Resend the collection WhatsApp to this customer now?')) {
        const rr = await adminAPI.resendWhatsApp(res.data.id);
        if (rr.data?.success) { toast.success('WhatsApp resent'); if (rr.data?.order) setSelectedOrder(rr.data.order); fetchOrders(); }
        else toast.error(rr.data?.detail || 'WhatsApp failed to send');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid cellphone number');
    } finally {
      setSavingPhone(false);
    }
  };

  const resendEmail = async () => {
    if (!selectedOrder) return;
    setResendingEmail(true);
    try {
      const res = await adminAPI.resendEmail(selectedOrder.id);
      if (res.data?.success) toast.success('Email resent'); else toast.error(res.data?.detail || 'Email failed to send');
      if (res.data?.order) setSelectedOrder(res.data.order);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResendingEmail(false);
    }
  };

  const resendWhatsApp = async () => {    if (!selectedOrder) return;
    const already = (selectedOrder.notification_log || []).some(
      (n) => n.channel === 'whatsapp' && n.event === 'ready_for_collection' && n.success
    );
    if (already && !window.confirm('This customer was already notified on WhatsApp. Resend anyway?')) return;
    setResending(true);
    try {
      const res = await adminAPI.resendWhatsApp(selectedOrder.id);
      if (res.data?.success) toast.success('WhatsApp resent');
      else toast.error(res.data?.detail || 'WhatsApp failed to send');
      if (res.data?.order) setSelectedOrder(res.data.order);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend WhatsApp');
    } finally {
      setResending(false);
    }
  };

  // Load the manual-send WhatsApp templates whenever the order modal opens.
  useEffect(() => {
    if (!selectedOrder) { setWaTemplates([]); setWaTemplateChoice(''); return; }
    let cancelled = false;
    setWaTemplatesLoading(true);
    adminAPI.getWhatsAppTemplates(selectedOrder.id)
      .then((r) => {
        if (cancelled) return;
        const tpls = r.data?.templates || [];
        setWaTemplates(tpls);
        setWaTemplatesTo(r.data?.to || '');
        const preferred = tpls.find((t) => t.recommended && t.configured) || tpls.find((t) => t.configured) || tpls[0];
        setWaTemplateChoice(preferred?.template || '');
      })
      .catch(() => { if (!cancelled) setWaTemplates([]); })
      .finally(() => { if (!cancelled) setWaTemplatesLoading(false); });
    return () => { cancelled = true; };
  }, [selectedOrder?.id]);

  const sendWhatsAppTemplate = async () => {
    if (!selectedOrder || !waTemplateChoice) return;
    const tpl = waTemplates.find((t) => t.template === waTemplateChoice);
    if (!tpl) return;
    if (!tpl.configured) { toast.error("This template isn't configured (no approved template name set)."); return; }
    if (!waTemplatesTo) { toast.error('This order has no contact cellphone number.'); return; }
    if (!tpl.recommended && !window.confirm(`"${tpl.label}" doesn't match this order's current stage. Send it anyway?`)) return;
    setWaSending(true);
    try {
      const res = await adminAPI.sendWhatsAppTemplate(selectedOrder.id, waTemplateChoice);
      if (res.data?.success) toast.success(`${tpl.label} WhatsApp sent`);
      else toast.error(res.data?.detail || 'WhatsApp failed to send');
      if (res.data?.order) setSelectedOrder(res.data.order);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send WhatsApp');
    } finally {
      setWaSending(false);
    }
  };

  const runBulkResendWA = async () => {
    if (selectedIds.size === 0) return;
    const n = selectedIds.size;
    if (!window.confirm(`Resend the collection WhatsApp to ${n} selected order${n > 1 ? 's' : ''}?\n\nOrders not in a collection stage are skipped${includeNotified ? '' : ', and orders already successfully notified are skipped'}.`)) return;
    setBulkBusy(true);
    try {
      const res = await adminAPI.bulkResendWhatsApp([...selectedIds], includeNotified);
      const c = res.data?.counts || {};
      const skipped = (c.skipped_not_collection || 0) + (c.skipped_already_sent || 0);
      const summary = `Resent ${c.sent || 0} · Failed ${c.failed || 0} · Skipped ${skipped} (${c.skipped_not_collection || 0} not in collection stage, ${c.skipped_already_sent || 0} already notified)`;
      if ((c.sent || 0) === 0 && (c.failed || 0) === 0 && (c.skipped_already_sent || 0) > 0 && !includeNotified) {
        toast.warning(`Nothing resent — ${c.skipped_already_sent} order${c.skipped_already_sent > 1 ? 's were' : ' was'} already notified. Tick “incl. already notified” to resend anyway.`);
      } else {
        toast.success(summary);
      }
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk resend failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulk = async () => {    if (!bulkTarget || selectedIds.size === 0) return;
    const n = selectedIds.size;
    const isDestructive = BULK_DESTRUCTIVE.includes(bulkTarget);
    const msg = isDestructive
      ? `Update Order Stage: ${statusLabel(bulkTarget)} for ${n} order${n > 1 ? 's' : ''}?\n\nThis notifies each customer and may issue refunds / store credit for any paid orders. This cannot be undone.`
      : `Update Order Stage to "${statusLabel(bulkTarget)}" for ${n} order${n > 1 ? 's' : ''}?\n\nEach customer will receive the notification for this stage.`;
    if (!window.confirm(msg)) return;
    setBulkBusy(true);
    try {
      const res = await adminAPI.bulkUpdateOrderStatus([...selectedIds], bulkTarget);
      const updated = (res.data.updated || []).length;
      const failed = (res.data.failed || []).length;
      if (updated) toast.success(`${updated} order${updated > 1 ? 's' : ''} updated`);
      if (failed) toast.error(`${failed} failed — see logs`);
      setSelectedIds(new Set());
      setBulkTarget('');
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const runConfirmStock = async () => {
    if (selectedIds.size === 0) return;
    const n = selectedIds.size;
    if (!window.confirm(`Confirm stock & request payment for ${n} order${n > 1 ? 's' : ''}?\n\nOnly stock-confirm ("pay after confirmation") orders that haven't been invoiced yet are affected. Each customer is sent a payment link by email & WhatsApp, which starts their pay-by clock.`)) return;
    setBulkBusy(true);
    try {
      const res = await adminAPI.confirmStockRequestPayment({ order_ids: [...selectedIds] });
      const u = (res.data.updated || []).length;
      const s = (res.data.skipped || []).length;
      if (u) toast.success(`${u} order${u > 1 ? 's' : ''} — payment requested`);
      else toast.info('No eligible stock-confirm orders in the selection');
      if (s) toast.message(`${s} skipped (not eligible)`);
      setSelectedIds(new Set());
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const runCancelMoq = async () => {
    if (selectedIds.size === 0) return;
    const n = selectedIds.size;
    if (!window.confirm(`Cancel ${n} order${n > 1 ? 's' : ''} — minimum not reached?\n\nOnly stock-confirm orders still awaiting confirmation are affected. Customers receive a "minimum not reached" notice (no payment was taken) and any applied credit is returned. This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const res = await adminAPI.cancelMoqNotMet({ order_ids: [...selectedIds] });
      const c = (res.data.cancelled || []).length;
      if (c) toast.success(`${c} order${c > 1 ? 's' : ''} cancelled (minimum not reached)`);
      else toast.info('No eligible stock-confirm orders in the selection');
      setSelectedIds(new Set());
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBulkBusy(false);
    }
  };
  // item; openBulkRefund pulls the checkbox-selected items; confirmRefund posts
  // the chosen product_ids + destination to the bulk endpoint (handles 1..N).
  const toggleRefundSel = (productId) => {
    setRefundSelIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId); else next.add(productId);
      return next;
    });
  };

  // Remaining (billable) units still refundable on a line item.
  const remainingQty = (item) => Math.max(0, (Number(item?.quantity) || 0) - (Number(item?.refunded_qty) || 0));

  const openRefund = (item) => {
    setRefundItems([item]);
    setRefundQty({ [item.product_id]: remainingQty(item) });
    setRefundDest('credit');
    setRefundNote('');
  };

  const openBulkRefund = () => {
    const items = (selectedOrder?.items || []).filter(
      (it) => refundSelIds.has(it.product_id) && !it.refunded
    );
    if (items.length === 0) return;
    setRefundItems(items);
    setRefundQty(Object.fromEntries(items.map((it) => [it.product_id, remainingQty(it)])));
    setRefundDest('credit');
    setRefundNote('');
  };

  const setRefundQtyFor = (item, val) => {
    const max = remainingQty(item);
    let n = parseInt(val, 10);
    if (Number.isNaN(n)) n = 1;
    n = Math.max(1, Math.min(max, n));
    setRefundQty((prev) => ({ ...prev, [item.product_id]: n }));
  };

  const refundTotal = refundItems.reduce(
    (s, it) => s + (it.price || 0) * (refundQty[it.product_id] ?? remainingQty(it)),
    0
  );

  const confirmRefund = async () => {
    if (!selectedOrder || refundItems.length === 0) return;
    const toCredit = refundDest === 'credit';
    const productIds = refundItems.map((it) => it.product_id);
    const quantities = Object.fromEntries(
      refundItems.map((it) => [it.product_id, refundQty[it.product_id] ?? remainingQty(it)])
    );
    const total = refundTotal;
    setRefundBusy(true);
    try {
      const res = await adminAPI.refundOrderItems(selectedOrder.id, productIds, refundNote, toCredit, quantities);
      const refunded = res.data?.total_refunded ?? total;
      if (res.data?.destination === 'store_credit') {
        toast.success(`R${money(refunded)} added to customer's store credit`);
      } else {
        toast.success(`Refunded R${money(refunded)} to the card via Paystack`);
      }
      setSelectedOrder(res.data?.order || null);
      setRefundItems([]);
      setRefundSelIds(new Set());
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setRefundBusy(false);
    }
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`Permanently DELETE order ${order.order_number || '#' + order.id.slice(0, 8)}?\nThis removes the order and its payment records from the database and cannot be undone.`)) return;
    setDeletingId(order.id);
    try {
      await adminAPI.deleteOrder(order.id);
      toast.success('Order deleted');
      if (selectedOrder?.id === order.id) setSelectedOrder(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete order');
    } finally {
      setDeletingId(null);
    }
  };

  const exportOrders = async () => {
    setExportingOrders(true);
    try {
      const params = {};
      const exStatus = excludeOf(statusOptions, statusFilter);
      if (exStatus.length) params.exclude_status = exStatus.join(',');
      const exPayment = excludeOf(PAYMENT_OPTIONS, paymentFilter);
      if (exPayment.length) params.exclude_payment = exPayment.join(',');
      if (selectedSuppliers.length) params.supplier_ids = selectedSuppliers.join(',');
      if (productFilter) params.product_id = productFilter.id;
      if (filterCollectionPoint && filterCollectionPoint !== 'all') params.collection_point_id = filterCollectionPoint;
      if (monthFilter.length) params.month = monthFilter.join(',');
      const res = await adminAPI.exportOrders(params);
      const cpName = filterCollectionPoint !== 'all'
        ? (collectionPoints.find(c => c.id === filterCollectionPoint)?.name || 'Point').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
        : 'All';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVZ_Orders_${cpName}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Orders exported');
    } catch {
      toast.error('Failed to export orders');
    } finally {
      setExportingOrders(false);
    }
  };

  const downloadAggregated = async (supplierId, supplierName) => {
    setExportingAgg(supplierId);
    try {
      const res = await adminAPI.exportAggregatedReport(supplierId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVZ_Aggregated_${supplierName.replace(/\s/g, '_')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Aggregated report downloaded');
    } catch {
      toast.error('Failed to export report');
    } finally {
      setExportingAgg(null);
    }
  };

  const downloadInvoice = async (orderId) => {
    try {
      const res = await adminAPI.downloadInvoice(orderId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVZ_Invoice_${orderId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download invoice');
    }
  };

  const [previewingId, setPreviewingId] = useState(null);
  const previewInvoice = async (orderId) => {
    setPreviewingId(orderId);
    try {
      const res = await adminAPI.downloadInvoice(orderId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      // Open the invoice inline so the admin can review it before downloading.
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Failed to preview invoice');
    } finally {
      setPreviewingId(null);
    }
  };

  return (
    <div data-testid="admin-orders">
      <div className="mb-8">
        <div className="mb-4">
          <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">Orders</h1>
          <p className="font-body text-sm text-foreground/70 mt-1">{filteredOrders.length}{filteredOrders.length !== orders.length ? ` of ${orders.length}` : ''} orders</p>
        </div>

        {/* Toolbar: large search + clearly grouped filters + export actions */}
        <div className="rounded-2xl border border-border bg-card/60 p-4 space-y-4">
          {/* Search — full width, prominent */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-foreground/40" />
            <Input
              data-testid="order-search-input"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search by customer name or order number..."
              className="w-full h-12 pl-12 rounded-xl bg-muted border-border font-body text-base"
            />
          </div>

          {productFilter && (
            <div
              data-testid="order-product-filter-banner"
              className="flex items-center gap-2 rounded-xl border border-teal/30 bg-teal/10 px-4 py-2.5 font-body text-sm text-foreground/80"
            >
              <Package className="h-4 w-4 text-teal shrink-0" />
              <span className="min-w-0">
                Showing open orders containing <span className="font-semibold text-foreground">{productFilter.name}</span>
                {productFilter.sku ? <span className="text-foreground/50"> ({productFilter.sku})</span> : null}.
                <span className="text-foreground/60"> Ready for Collection, Collection Notified and Closed orders are hidden while this filter is active.</span>
              </span>
            </div>
          )}

          {/* Filters row */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-foreground/70 mr-1" data-testid="orders-filters-label">
              <Filter className="h-4 w-4 text-teal" />
              <span className="font-body text-xs font-semibold uppercase tracking-wider">Filters</span>
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  data-testid="order-supplier-filter"
                  className="w-full sm:w-56 justify-between rounded-xl bg-card border-border font-body font-normal text-sm"
                >
                  <span className="truncate">
                    {selectedSuppliers.length === 0
                      ? 'All Suppliers'
                      : selectedSuppliers.length === 1
                        ? (suppliers.find((s) => s.id === selectedSuppliers[0])?.name || '1 supplier')
                        : `${selectedSuppliers.length} suppliers selected`}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-xl" align="start">
                <Command>
                  <CommandInput placeholder="Search suppliers..." data-testid="order-supplier-search" className="font-body" />
                  <CommandList>
                    <CommandEmpty className="font-body text-sm py-4 text-center text-foreground/50">No suppliers found.</CommandEmpty>
                    <CommandItem
                      value="All Suppliers"
                      onSelect={() => setSelectedSuppliers([])}
                      data-testid="order-supplier-option-all"
                      className="font-body cursor-pointer"
                    >
                      <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded border ${selectedSuppliers.length === 0 ? 'bg-teal border-teal' : 'border-border'}`}>
                        {selectedSuppliers.length === 0 && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                      </div>
                      All Suppliers
                    </CommandItem>
                    {[...suppliers].sort((a,b)=>(a.name||'').localeCompare(b.name||'',undefined,{sensitivity:'base'})).map((s) => {
                      const checked = selectedSuppliers.includes(s.id);
                      return (
                        <CommandItem
                          key={s.id}
                          value={s.name}
                          onSelect={() => toggleSupplier(s.id)}
                          data-testid={`order-supplier-option-${s.id}`}
                          className="font-body cursor-pointer"
                        >
                          <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded border ${checked ? 'bg-teal border-teal' : 'border-border'}`}>
                            {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                          </div>
                          {s.name}
                        </CommandItem>
                      );
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {selectedSuppliers.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="order-supplier-clear-btn"
                onClick={() => setSelectedSuppliers([])}
                className="rounded-full text-xs text-foreground/60 hover:text-foreground gap-1"
              >
                <X className="h-3.5 w-3.5" /> Clear
              </Button>
            )}

            <Button
              variant={waFailedOnly ? 'default' : 'outline'}
              size="sm"
              data-testid="orders-wa-failed-toggle"
              onClick={() => setWaFailedOnly((v) => !v)}
              className={`rounded-xl font-body text-sm gap-1.5 ${waFailedOnly ? 'bg-red-600 hover:bg-red-700 text-white border-red-600' : 'bg-card border-border text-foreground/70'}`}
              title="Show only orders whose collection WhatsApp failed"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              WhatsApp failed
            </Button>

            {/* Product search — pick a specific product to see every open order
                containing it (fulfilled/closed stages are excluded). */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  data-testid="order-product-filter"
                  className="w-full sm:w-60 justify-between rounded-xl bg-card border-border font-body font-normal text-sm"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Package className="h-3.5 w-3.5 text-teal shrink-0" />
                    <span className="truncate">{productFilter ? productFilter.name : 'Any Product'}</span>
                  </span>
                  <ChevronsUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[18rem] p-0 rounded-xl" align="start">
                <Command>
                  <CommandInput placeholder="Search products by name or SKU..." data-testid="order-product-search" className="font-body" />
                  <CommandList>
                    <CommandEmpty className="font-body text-sm py-4 text-center text-foreground/50">No products found.</CommandEmpty>
                    {[...products].sort((a,b)=>(a.name||'').localeCompare(b.name||'',undefined,{sensitivity:'base'})).map((p) => {
                      const checked = productFilter?.id === p.id;
                      return (
                        <CommandItem
                          key={p.id}
                          value={`${p.name || ''} ${p.sku || ''}`}
                          onSelect={() => setProductFilter(checked ? null : { id: p.id, name: p.name, sku: p.sku })}
                          data-testid={`order-product-option-${p.id}`}
                          className="font-body cursor-pointer"
                        >
                          <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded border shrink-0 ${checked ? 'bg-teal border-teal' : 'border-border'}`}>
                            {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                          </div>
                          <span className="truncate">
                            {p.name}
                            {p.sku ? <span className="text-foreground/40 ml-1">· {p.sku}</span> : null}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {productFilter && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="order-product-clear-btn"
                onClick={() => setProductFilter(null)}
                className="rounded-full text-xs text-foreground/60 hover:text-foreground gap-1"
              >
                <X className="h-3.5 w-3.5" /> Clear
              </Button>
            )}

            <Select value={filterCollectionPoint} onValueChange={setFilterCollectionPoint}>
              <SelectTrigger data-testid="order-collection-filter" className="w-full sm:w-52 rounded-xl bg-card border-border font-body text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin className="h-3.5 w-3.5 text-teal shrink-0" />
                  <SelectValue placeholder="Collection point" />
                </div>
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all" className="font-body">All Collection Points</SelectItem>
                {[...collectionPoints].sort((a,b)=>(a.name||'').localeCompare(b.name||'',undefined,{sensitivity:'base'})).map(c => (
                  <SelectItem key={c.id} value={c.id} className="font-body">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <MultiSelectFilter
              testid="order-status-filter"
              icon={Layers}
              allLabel="All Stages"
              options={statusOptions}
              selected={statusFilter}
              onChange={setStatusFilter}
              renderOption={(s) => statusLabel(s)}
              width="sm:w-44"
              disabled={!!productFilter}
            />

            <MultiSelectFilter
              testid="order-payment-filter"
              icon={CreditCard}
              allLabel="All Payments"
              options={PAYMENT_OPTIONS}
              selected={paymentFilter}
              onChange={setPaymentFilter}
              renderOption={(p) => paymentLabel(p)}
              width="sm:w-44"
            />

            <MultiSelectFilter
              testid="order-month-filter"
              icon={FileText}
              allLabel="All Periods"
              options={monthOptions}
              selected={monthFilter}
              onChange={setMonthFilter}
              renderOption={(m) => monthLabelShort(m)}
              width="sm:w-40"
            />

            {/* Export actions — full width on mobile, pushed right on desktop */}
            <div className="w-full sm:w-auto sm:ml-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="columns-toggle-btn"
                    className="w-full sm:w-auto justify-center rounded-xl bg-card border-border text-foreground/80 hover:text-foreground hover:bg-muted font-body text-sm gap-2"
                  >
                    <Columns3 className="h-3.5 w-3.5 text-teal" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl w-48">
                  <DropdownMenuLabel className="font-body text-xs text-foreground/60">Show columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {TOGGLE_COLUMNS.map(c => (
                    <DropdownMenuCheckboxItem
                      key={c.key}
                      checked={colVisible(c.key)}
                      onCheckedChange={() => toggleColumn(c.key)}
                      onSelect={(e) => e.preventDefault()}
                      data-testid={`column-toggle-${c.key}`}
                      className="font-body text-sm"
                    >
                      {c.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Select onValueChange={(v) => {
                const sup = suppliers.find(s => s.id === v);
                if (sup) downloadAggregated(sup.id, sup.name);
              }}>
                <SelectTrigger data-testid="aggregated-export-trigger" className="w-full sm:w-52 rounded-xl bg-card border-border font-body text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileSpreadsheet className="h-3.5 w-3.5 text-teal shrink-0" />
                    <span className="truncate">Aggregated Export</span>
                  </div>
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {[...suppliers].sort((a,b)=>(a.name||'').localeCompare(b.name||'',undefined,{sensitivity:'base'})).map(s => (
                    <SelectItem key={s.id} value={s.id} className="font-body text-sm">{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={exportOrders}
                disabled={exportingOrders}
                data-testid="export-orders-btn"
                className="w-full sm:w-auto justify-center rounded-xl bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2"
              >
                {exportingOrders ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
                Export Orders
              </Button>
            </div>
          </div>
        </div>
      </div>

      {!loading && filteredOrders.length > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="mb-3 flex flex-wrap items-center gap-3 p-3 rounded-xl bg-card border border-border"
        >
          <Layers className="h-4 w-4 text-teal" />
          <span className="font-body text-sm text-foreground/70">
            <span data-testid="bulk-selection-count" className="font-semibold text-foreground">{selectedIds.size}</span>{' '}
            selected
          </span>
          <div className="relative flex items-center">
            <Select value={bulkTarget} onValueChange={setBulkTarget} disabled={selectedIds.size === 0}>
              <SelectTrigger
                data-testid="bulk-status-select"
                className="w-full sm:w-56 rounded-full bg-muted border-border font-body text-sm pr-9"
              >
                <SelectValue placeholder="Update Order Stage..." />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {BULK_TARGETS.map(s => (
                  <SelectItem key={s} value={s} className="font-body">{statusLabel(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {bulkTarget && (
              <button
                type="button"
                data-testid="bulk-status-clear-btn"
                onClick={() => setBulkTarget('')}
                title="Clear stage selection"
                className="absolute right-8 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-foreground/50 hover:text-foreground hover:bg-muted-foreground/10 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Button
            data-testid="bulk-apply-btn"
            onClick={runBulk}
            disabled={!bulkTarget || selectedIds.size === 0 || bulkBusy}
            size="sm"
            className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm font-bold"
          >
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
          </Button>
          <div className="hidden sm:block h-6 w-px bg-border" />
          <Button
            data-testid="bulk-confirm-stock-btn"
            onClick={runConfirmStock}
            disabled={selectedIds.size === 0 || bulkBusy}
            size="sm"
            className="rounded-full bg-teal/15 border border-teal/70 text-teal hover:bg-teal-dark hover:text-white hover:border-teal-dark font-body text-sm font-semibold disabled:opacity-60"
            title="Stock-confirm suppliers: confirm stock and send the payment link"
          >
            Confirm Stock &amp; Request Payment
          </Button>
          <Button
            data-testid="bulk-cancel-moq-btn"
            onClick={runCancelMoq}
            disabled={selectedIds.size === 0 || bulkBusy}
            size="sm"
            className="rounded-full bg-red-500/15 border border-red-400/70 text-red-300 hover:bg-red-700 hover:text-white hover:border-red-700 font-body text-sm font-semibold disabled:opacity-60"
            title="Stock-confirm suppliers: cancel orders because the minimum wasn't reached"
          >
            Cancel — MOQ Not Met
          </Button>
          <div className="hidden sm:block h-6 w-px bg-border" />
          <Button
            data-testid="bulk-resend-wa-btn"
            onClick={runBulkResendWA}
            disabled={selectedIds.size === 0 || bulkBusy}
            size="sm"
            className="rounded-full bg-teal/15 border border-teal/70 text-teal hover:bg-teal-dark hover:text-white hover:border-teal-dark font-body text-sm font-semibold disabled:opacity-60 gap-1.5"
            title="Resend the collection WhatsApp to selected orders that are in a collection stage"
          >
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
            Resend WhatsApp
          </Button>
          <label className="flex items-center gap-1.5 font-body text-xs text-foreground/60 cursor-pointer select-none" data-testid="bulk-resend-include-label">
            <Checkbox
              data-testid="bulk-resend-include-notified"
              checked={includeNotified}
              onCheckedChange={(v) => setIncludeNotified(!!v)}
              className="h-3.5 w-3.5"
            />
            incl. already notified
          </label>
          {selectedIds.size > 0 && (
            <button
              data-testid="bulk-clear-btn"
              onClick={() => setSelectedIds(new Set())}
              className="font-body text-xs text-foreground/50 hover:text-foreground/80 underline ml-auto"
            >
              Clear selection
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-16 rounded-2xl bg-muted animate-pulse" />)}
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-muted/30 border border-border/50">
          <p className="font-heading text-xl text-foreground/50">No orders found</p>
        </div>
      ) : (
        <>
        {(() => {
          const counts = filteredOrders.reduce((a, o) => {
            const s = waCollectionStatus(o).state;
            if (['sent', 'delivered', 'read', 'failed', 'pending'].includes(s)) a[s] = (a[s] || 0) + 1;
            return a;
          }, {});
          const total = Object.values(counts).reduce((x, y) => x + y, 0);
          if (!total) return null;
          const cards = [
            { key: 'read', label: 'Read', cls: 'text-teal' },
            { key: 'delivered', label: 'Delivered', cls: 'text-teal' },
            { key: 'sent', label: 'Sent', cls: 'text-foreground/70' },
            { key: 'pending', label: 'Not sent', cls: 'text-amber-300' },
            { key: 'failed', label: 'Failed', cls: 'text-red-400' },
          ];
          return (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="wa-delivery-summary">
              <span className="font-body text-xs text-foreground/50 self-center mr-1">Collection WhatsApp (current view):</span>
              {cards.map((c) => (
                <div key={c.key} className="rounded-xl border border-border bg-card px-3 py-1.5 flex items-baseline gap-1.5" data-testid={`wa-summary-${c.key}`}>
                  <span className={`font-heading text-lg font-semibold ${c.cls}`}>{counts[c.key] || 0}</span>
                  <span className="font-body text-[11px] text-foreground/60">{c.label}</span>
                </div>
              ))}
            </div>
          );
        })()}
        <div className="overflow-x-auto rounded-2xl border border-border" data-testid="orders-table-scroll">
          <table className="w-full" style={{ minWidth: `${tableMinWidth}px` }} data-testid="orders-table">
            <thead>
              <tr className="bg-card border-b border-border">
                <th className="p-4 w-12">
                  <Checkbox
                    data-testid="bulk-select-all"
                    checked={selectedIds.size > 0 && selectedIds.size === filteredOrders.length}
                    onCheckedChange={toggleAll}
                  />
                </th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4 w-[170px] whitespace-nowrap">Order ID</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4 w-[182px]">Customer</th>
                {colVisible('supplier') && <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Supplier</th>}
                {colVisible('collection') && <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Collection</th>}
                {colVisible('items') && <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Items</th>}
                {colVisible('stage') && <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Order Stage</th>}
                {colVisible('payment') && <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Payment</th>}
                {colVisible('whatsapp') && <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">WhatsApp</th>}
                {colVisible('total') && <th className="text-right font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Total</th>}
                {colVisible('credit') && <th className="text-right font-body text-xs uppercase tracking-wider text-foreground/60 p-4 whitespace-nowrap">Credit</th>}
                {colVisible('cash') && <th className="text-right font-body text-xs uppercase tracking-wider text-foreground/60 p-4 whitespace-nowrap">Cash Payable</th>}
                {colVisible('date') && <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Date</th>}
                <th className="text-right font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageOrders.map(order => {
                const ci = creditInfo(order);
                return (
                <tr key={order.id} onClick={() => setSelectedOrder(order)} className="group border-b border-border/50 last:border-0 hover:bg-muted/30 cursor-pointer" data-testid={`admin-order-${order.id}`}>
                  <td className="p-4 w-12" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      data-testid={`bulk-select-${order.id}`}
                      checked={selectedIds.has(order.id)}
                      onCheckedChange={() => toggleRow(order.id)}
                    />
                  </td>
                  <td className="p-4 w-[170px] font-body text-sm text-foreground font-medium">
                    <span className="whitespace-nowrap">{order.order_number || `#${order.id.slice(0, 8)}`}</span>
                  </td>
                  <td className="p-4 w-[182px]">
                    <div className="w-[150px]">
                      <p className="font-body text-sm text-foreground truncate">{order.contact_name || order.user_name || '-'}</p>
                      <p className="font-body text-xs text-foreground/50 truncate">{order.contact_phone || ''}</p>
                    </div>
                  </td>
                  {colVisible('supplier') && (
                  <td className="p-4">
                    {(() => {
                      const names = [...new Set((order.items || []).map(it => it.supplier_name).filter(Boolean))];
                      if (names.length === 0) return <span className="font-body text-xs text-foreground/40">—</span>;
                      return (
                        <div className="flex items-center gap-1.5" data-testid={`order-supplier-${order.id}`}>
                          <Truck className="h-3 w-3 text-vintage flex-shrink-0" />
                          <span className="font-body text-xs text-foreground/70">
                            {names[0]}{names.length > 1 ? ` +${names.length - 1}` : ''}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  )}
                  {colVisible('collection') && (
                  <td className="p-4">
                    {order.collection_point_name ? (
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-3 w-3 text-teal" />
                        <span className="font-body text-xs text-foreground/70">{order.collection_point_name}</span>
                      </div>
                    ) : (
                      <span className="font-body text-xs text-foreground/40">—</span>
                    )}
                  </td>
                  )}
                  {colVisible('items') && (
                  <td className="p-4 font-body text-sm text-foreground/70">
                    {(() => {
                      const n = order.items?.length || 0;
                      const u = (order.items || []).reduce((s, it) => s + (it.quantity || 0), 0);
                      return (<>{n} item{n === 1 ? '' : 's'}<span className="text-foreground/40"> ({u} unit{u === 1 ? '' : 's'})</span></>);
                    })()}
                  </td>
                  )}
                  {colVisible('stage') && (
                  <td className="p-4" onClick={(e) => e.stopPropagation()}>
                    <Select value={normalizeStatus(order.status)} onValueChange={(v) => updateStatus(order.id, v)} disabled={updating}>
                      <SelectTrigger className={`w-44 rounded-full text-xs font-body border ${statusColor(order.status)}`} data-testid={`order-status-${order.id}`}>
                        <SelectValue>{statusLabel(order.status)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        {PIPELINE_STATUSES.map(s => (
                          <SelectItem key={s} value={s} className="font-body text-sm">{statusLabel(s)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  )}
                  {colVisible('payment') && (
                  <td className="p-4" onClick={(e) => e.stopPropagation()}>
                    <Select value={order.payment_status || 'unpaid'} onValueChange={(v) => updatePayment(order.id, v)} disabled={payingId === order.id}>
                      <SelectTrigger className={`w-40 rounded-full text-xs font-body border ${paymentColor(order.payment_status)}`} data-testid={`order-payment-${order.id}`}>
                        {payingId === order.id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <SelectValue>{paymentLabel(order.payment_status)}</SelectValue>}
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        {PAYMENT_OPTIONS.map(p => (
                          <SelectItem key={p} value={p} className="font-body text-sm">{paymentLabel(p)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {order.payment_method === 'eft' && (order.payment_status || 'unpaid') !== 'paid' && (
                      <span className="mt-1 font-body text-[10px] text-gold/80 inline-flex items-center gap-1" data-testid={`order-eft-hint-${order.id}`}>
                        <Landmark className="h-2.5 w-2.5" strokeWidth={2.5} /> EFT
                      </span>
                    )}
                    {ci.hasCredit && (
                      <span
                        className={`mt-1 font-body text-[10px] inline-flex items-center gap-1 ${ci.partialUnpaid ? 'text-amber-300' : 'text-teal'}`}
                        data-testid={`order-credit-tag-${order.id}`}
                        title={ci.fullyCovered ? 'Settled by store credit' : 'Partly paid by store credit'}
                      >
                        <Wallet className="h-2.5 w-2.5" strokeWidth={2.5} />
                        {ci.fullyCovered ? 'paid by credit' : `credit −R${money(ci.creditApplied)}`}
                      </span>
                    )}
                  </td>
                  )}
                  {colVisible('whatsapp') && (
                  <td className="p-4" onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const w = waCollectionStatus(order);
                      const canResend = ['ready_for_collection', 'collection_notification_sent'].includes(order.status);
                      return (
                        <div className="flex items-center gap-1.5" data-testid={`order-wa-cell-${order.id}`}>
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-body text-[10px] font-semibold ${w.cls}`} data-testid={`order-wa-status-${order.id}`}>{w.label}</span>
                          {canResend && (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={rowResendId === order.id}
                              onClick={() => resendWhatsAppRow(order)}
                              className="h-7 w-7 rounded-full hover:bg-teal/10"
                              data-testid={`row-resend-wa-${order.id}`}
                              title="Resend collection WhatsApp"
                            >
                              {rowResendId === order.id ? <Loader2 className="h-3.5 w-3.5 animate-spin text-teal" /> : <MessageCircle className="h-3.5 w-3.5 text-teal" />}
                            </Button>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  )}
                  {colVisible('total') && (
                  <td className="p-4 text-right font-body text-sm font-semibold text-vintage">
                    R{money(order.total)}
                    {ci.partialUnpaid && (
                      <span className="block font-body text-[10px] font-normal text-amber-300" data-testid={`order-amount-due-${order.id}`}>
                        R{money(ci.amountPayable)} due
                      </span>
                    )}
                  </td>
                  )}
                  {colVisible('credit') && (
                  <td className="p-4 text-right" data-testid={`order-credit-col-${order.id}`}>
                    {ci.hasCredit ? (
                      <span className="font-body text-sm font-semibold text-teal inline-flex items-center gap-1 justify-end">
                        <Wallet className="h-3 w-3" strokeWidth={2.5} />
                        −R{money(ci.creditApplied)}
                      </span>
                    ) : (
                      <span className="font-body text-sm text-foreground/30">—</span>
                    )}
                  </td>
                  )}
                  {colVisible('cash') && (
                  <td className="p-4 text-right" data-testid={`order-cash-col-${order.id}`}>
                    {(() => {
                      const ci = creditInfo(order);
                      const cls = ci.fullyCovered ? 'text-teal' : ci.partialUnpaid ? 'text-amber-300' : 'text-foreground';
                      return (
                        <>
                          <span className={`font-body text-sm font-bold ${cls}`}>R{money(ci.amountPayable)}</span>
                          {ci.partialUnpaid && (
                            <span className="block font-body text-[10px] font-semibold text-amber-300">still due</span>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  )}
                  {colVisible('date') && (
                  <td className="p-4 font-body text-xs text-foreground/60">
                    {new Date(order.created_at).toLocaleDateString('en-ZA', { timeZone: 'Africa/Johannesburg' })}
                  </td>
                  )}
                  <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {['confirmed', 'packing', 'ready_for_collection', 'collection_notification_sent', 'closed'].includes(order.status) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => downloadInvoice(order.id)}
                          className="h-8 w-8 hover:bg-muted rounded-full"
                          data-testid={`invoice-${order.id}`}
                          title="Download Invoice"
                        >
                          <FileText className="h-3.5 w-3.5 text-teal" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSelectedOrder(order)}
                        className="h-8 w-8 hover:bg-muted rounded-full"
                        data-testid={`view-order-${order.id}`}
                      >
                        <Eye className="h-3.5 w-3.5 text-foreground/70" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteOrder(order)}
                        disabled={deletingId === order.id}
                        className="h-8 w-8 hover:bg-red-900/20 rounded-full"
                        data-testid={`delete-order-${order.id}`}
                        title="Delete order"
                      >
                        {deletingId === order.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-red-400" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
      {!loading && filteredOrders.length > 0 && (
        <TablePagination {...ordersPg} testid="orders-pagination" />
      )}

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => { setSelectedOrder(null); setRefundSelIds(new Set()); setEditingPhone(false); }}>
        <DialogContent className="bg-background rounded-2xl max-w-lg" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              Order #{selectedOrder?.id?.slice(0, 8)}
            </DialogTitle>
            <DialogDescription className="sr-only">Order details, payment, items and refund actions.</DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-body text-xs text-foreground/60 uppercase tracking-wide">Customer</p>
                  <p className="font-body text-sm text-foreground">{selectedOrder.contact_name || selectedOrder.user_name || '-'}</p>
                  <p className="font-body text-xs text-foreground/50">{selectedOrder.contact_email || selectedOrder.user_email || ''}</p>
                  {editingPhone ? (
                    <div className="mt-1 flex items-center gap-1.5" data-testid="order-phone-edit">
                      <Input
                        data-testid="order-phone-input"
                        value={phoneInput}
                        onChange={(e) => setPhoneInput(e.target.value)}
                        placeholder="0821234567"
                        className="h-7 w-36 font-body text-xs rounded-lg bg-muted border-border"
                      />
                      <Button
                        data-testid="order-phone-save"
                        size="sm"
                        disabled={savingPhone}
                        onClick={saveContactPhone}
                        className="h-7 px-2 rounded-lg bg-teal hover:bg-teal-dark text-white"
                      >
                        {savingPhone ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                      </Button>
                      <Button
                        data-testid="order-phone-cancel"
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingPhone(false)}
                        className="h-7 px-2 rounded-lg text-foreground/60"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <p className="font-body text-xs text-foreground/50" data-testid="order-contact-phone">{selectedOrder.contact_phone || '—'}</p>
                      <button
                        type="button"
                        data-testid="order-phone-edit-btn"
                        onClick={() => { setPhoneInput(selectedOrder.contact_phone || ''); setEditingPhone(true); }}
                        className="text-foreground/40 hover:text-teal transition-colors"
                        title="Edit contact number"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  {selectedOrder.user_id && (
                    <Link
                      to={`/admin/users?q=${encodeURIComponent(selectedOrder.user_email || selectedOrder.contact_email || '')}`}
                      data-testid="order-customer-credit-link"
                      className="mt-1.5 inline-flex items-center gap-1.5 font-body text-xs text-teal hover:text-teal/80 hover:underline"
                    >
                      <Wallet className="h-3 w-3" />
                      Store credit balance: {custCredit == null ? '…' : `R${money(custCredit)}`}
                    </Link>
                  )}
                </div>
                <div>
                  <p className="font-body text-xs text-foreground/60 uppercase tracking-wide">Collection</p>
                  <div className="flex items-center gap-1 mt-1">
                    <MapPin className="h-3 w-3 text-teal" />
                    <p className="font-body text-sm text-foreground">
                      {selectedOrder.collection_point_name || 'Not specified'}
                    </p>
                  </div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap" data-testid="order-whatsapp-status">
                    <p className="font-body text-xs">
                      {(() => {
                        const waLog = (selectedOrder.notification_log || []).filter(
                          (n) => n.channel === 'whatsapp' && n.event === 'ready_for_collection'
                        );
                        const wa = waLog[waLog.length - 1];
                        const inCollection = ['ready_for_collection', 'collection_notification_sent'].includes(selectedOrder.status);
                        if (wa?.success) {
                          const d = wa.at ? new Date(wa.at).toLocaleDateString('en-ZA', { timeZone: 'Africa/Johannesburg' }) : '';
                          return <span className="text-teal">Collection WhatsApp sent{d ? ` ${d}` : ''}</span>;
                        }
                        if (wa && !wa.success) return <span className="text-red-400">Collection WhatsApp failed to send</span>;
                        if (inCollection) return <span className="text-amber-300">Collection WhatsApp not sent yet</span>;
                        return <span className="text-foreground/40">Collection WhatsApp sends at the “Ready for Collection” stage</span>;
                      })()}
                    </p>
                    {['ready_for_collection', 'collection_notification_sent'].includes(selectedOrder.status) && (
                      <Button
                        data-testid="resend-whatsapp-btn"
                        onClick={resendWhatsApp}
                        disabled={resending}
                        size="sm"
                        variant="outline"
                        className="h-6 rounded-full border-teal/40 text-teal hover:bg-teal/10 font-body text-[11px] px-2 gap-1"
                      >
                        {resending ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageCircle className="h-3 w-3" />}
                        Resend WhatsApp
                      </Button>
                    )}
                    {['ready_for_collection', 'collection_notification_sent'].includes(selectedOrder.status) && (
                      <Button
                        data-testid="resend-email-btn"
                        onClick={resendEmail}
                        disabled={resendingEmail}
                        size="sm"
                        variant="outline"
                        className="h-6 rounded-full border-gold/40 text-gold hover:bg-gold/10 font-body text-[11px] px-2 gap-1"
                      >
                        {resendingEmail ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                        Resend Email
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 border-t border-border pt-4" data-testid="order-detail-meta">
                <div>
                  <p className="font-body text-xs text-foreground/60 uppercase tracking-wide">Date &amp; Time</p>
                  <p className="font-body text-sm text-foreground" data-testid="order-detail-date">
                    {new Date(selectedOrder.created_at).toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg' })}
                  </p>
                </div>
                <div>
                  <p className="font-body text-xs text-foreground/60 uppercase tracking-wide">Payment Method</p>
                  <p className="font-body text-sm text-foreground" data-testid="order-detail-payment-method">
                    {selectedOrder.payment_method === 'eft' ? 'EFT (Bank Transfer)' : 'Card (Paystack)'}
                  </p>
                </div>
                <div>
                  <p className="font-body text-xs text-foreground/60 uppercase tracking-wide">Payment Status</p>
                  <Badge
                    data-testid="order-detail-payment-status"
                    className={`mt-1 rounded-full font-body text-[11px] border ${paymentColor(selectedOrder.payment_status)}`}
                  >
                    {paymentLabel(selectedOrder.payment_status)}
                  </Badge>
                </div>
              </div>

              {creditInfo(selectedOrder).hasCredit && (() => {
                const ci = creditInfo(selectedOrder);
                if (ci.partialUnpaid) {
                  return (
                    <div className="rounded-xl bg-amber-400/10 border border-amber-400/30 p-3 flex items-start gap-2" data-testid="order-credit-banner">
                      <Wallet className="h-4 w-4 text-amber-300 flex-shrink-0 mt-0.5" />
                      <p className="font-body text-xs text-amber-200">
                        <span className="font-semibold">R{money(ci.amountPayable)} still due</span> · R{money(ci.creditApplied)} already paid by store credit.
                        Only collect the amount payable — do not request the full total of R{money(selectedOrder.total)}.
                      </p>
                    </div>
                  );
                }
                if (ci.fullyCovered) {
                  return (
                    <div className="rounded-xl bg-teal/10 border border-teal/30 p-3 flex items-start gap-2" data-testid="order-credit-banner">
                      <Wallet className="h-4 w-4 text-teal flex-shrink-0 mt-0.5" />
                      <p className="font-body text-xs text-teal">
                        <span className="font-semibold">Settled by store credit</span> — R{money(ci.creditApplied)} applied, no cash due. No payment request needed.
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="rounded-xl bg-teal/10 border border-teal/30 p-3 flex items-start gap-2" data-testid="order-credit-banner">
                    <Wallet className="h-4 w-4 text-teal flex-shrink-0 mt-0.5" />
                    <p className="font-body text-xs text-teal">
                      R{money(ci.creditApplied)} of this order was paid using store credit.
                    </p>
                  </div>
                );
              })()}

              {/* Independent Order Stage + Payment controls */}
              <div className="grid grid-cols-2 gap-3 border-t border-border pt-4" data-testid="dialog-controls">
                <div>
                  <p className="font-body text-xs text-foreground/60 uppercase tracking-wide mb-1.5">Order Stage</p>
                  <Select value={normalizeStatus(selectedOrder.status)} onValueChange={(v) => updateStatus(selectedOrder.id, v)} disabled={updating}>
                    <SelectTrigger className={`w-full rounded-full text-xs font-body border ${statusColor(selectedOrder.status)}`} data-testid="dialog-status-select">
                      <SelectValue>{statusLabel(selectedOrder.status)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {PIPELINE_STATUSES.map(s => (
                        <SelectItem key={s} value={s} className="font-body text-sm">{statusLabel(s)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="font-body text-xs text-foreground/60 uppercase tracking-wide mb-1.5">Payment</p>
                  <Select value={selectedOrder.payment_status || 'unpaid'} onValueChange={(v) => updatePayment(selectedOrder.id, v)} disabled={payingId === selectedOrder.id}>
                    <SelectTrigger className={`w-full rounded-full text-xs font-body border ${paymentColor(selectedOrder.payment_status)}`} data-testid="dialog-payment-select">
                      {payingId === selectedOrder.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <SelectValue>{paymentLabel(selectedOrder.payment_status)}</SelectValue>}
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {PAYMENT_OPTIONS.map(p => (
                        <SelectItem key={p} value={p} className="font-body text-sm">{paymentLabel(p)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedOrder.payment_method === 'eft' && (
                    <p className="font-body text-[10px] text-gold/80 mt-1 inline-flex items-center gap-1">
                      <Landmark className="h-2.5 w-2.5" strokeWidth={2.5} /> EFT — set to Paid once funds reflect
                    </p>
                  )}
                </div>
              </div>
              {['cancelled', 'expired'].includes(normalizeStatus(selectedOrder.status)) && (
                <div className="border-t border-border pt-4" data-testid="reinstate-block">
                  <div className="rounded-xl bg-orange-400/10 border border-orange-400/30 p-3">
                    <p className="font-body text-xs text-orange-300">
                      {normalizeStatus(selectedOrder.status) === 'expired'
                        ? 'Expired: order window closed without payment.'
                        : (selectedOrder.auto_cancelled
                          ? 'Auto-cancelled: order window closed without payment.'
                          : 'This order is cancelled.')}
                    </p>
                    <Button
                      onClick={() => reinstateOrder(selectedOrder.id)}
                      disabled={updating}
                      data-testid="reinstate-order-btn"
                      className="mt-2 rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-xs gap-1.5 h-8"
                    >
                      {updating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                      Reinstate Order
                    </Button>
                  </div>
                </div>
              )}
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-body text-xs text-foreground/60 uppercase tracking-wide">Items</p>
                  {refundSelIds.size > 0 && (
                    <Button
                      data-testid="bulk-refund-btn"
                      onClick={openBulkRefund}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-red-700/40 text-red-300 hover:bg-red-900/20 font-body text-xs h-7 px-3"
                    >
                      Refund Selected ({refundSelIds.size})
                    </Button>
                  )}
                </div>
                {selectedOrder.items?.map((item, i) => {
                  const isPaid = (selectedOrder.payment_status || '') === 'paid';
                  const canRefund = isPaid && !item.refunded;
                  const isMatch = productFilter && item.product_id === productFilter.id;
                  return (
                    <div
                      key={i}
                      data-testid={`admin-order-item-${i}`}
                      className={`flex justify-between items-start py-2 gap-2 ${item.refunded ? 'opacity-60' : ''} ${isMatch ? 'rounded-lg ring-1 ring-teal/50 bg-teal/10 px-2.5 -mx-2.5' : ''}`}
                    >
                      <div className="flex items-start gap-2.5 min-w-0 flex-1">
                        {canRefund && (
                          <Checkbox
                            data-testid={`refund-select-${i}`}
                            checked={refundSelIds.has(item.product_id)}
                            onCheckedChange={() => toggleRefundSel(item.product_id)}
                            className="flex-shrink-0 mt-0.5 border-red-700/50 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                          />
                        )}
                        <div className="min-w-0">
                          <p className={`font-body text-sm text-foreground break-words ${item.refunded ? 'line-through' : ''}`}>
                            {item.name}
                            {formatUnitSize(item) && (
                              <span className="ml-2 font-body text-xs text-gold/80" data-testid={`admin-item-unit-${i}`}>
                                {formatUnitSize(item)}
                              </span>
                            )}
                            {isMatch && (
                              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-teal/20 text-teal px-2 py-0.5 font-body text-[10px] font-semibold uppercase tracking-wide align-middle" data-testid={`admin-item-searched-${i}`}>
                                <Package className="h-2.5 w-2.5" /> Searched
                              </span>
                            )}
                          </p>
                          <p className="font-body text-xs text-foreground/50">
                            Qty: {item.quantity} × R{money((item.price || 0))}
                            {!item.refunded && (item.refunded_qty || 0) > 0 && (
                              <span className="ml-2 text-amber-400" data-testid={`admin-item-partial-${i}`}>
                                · {item.refunded_qty} of {item.quantity} refunded (R{money((item.refunded_amount || 0))})
                              </span>
                            )}
                            {item.refunded && (
                              <span className="ml-2 text-red-400">
                                · Refunded R{money((item.refunded_amount || 0))}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 mt-px">
                        <p className="font-body text-sm font-semibold text-vintage whitespace-nowrap">
                          R{money((item.price * item.quantity))}
                        </p>
                        {canRefund && (
                          <Button
                            data-testid={`refund-item-btn-${i}`}
                            onClick={() => openRefund(item)}
                            size="sm"
                            variant="outline"
                            className="rounded-full border-red-700/40 text-red-300 hover:bg-red-900/20 font-body text-xs h-7 px-2"
                          >
                            Refund
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div className="border-t border-border pt-3 mt-3 space-y-1.5" data-testid="order-detail-breakdown">
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-foreground/60">Items subtotal</span>
                    <span className="text-foreground">R{money((selectedOrder.items_subtotal ?? 0))}</span>
                  </div>
                  {(selectedOrder.delivery_lines || []).map((d, i) => (
                    <div key={i} className="flex justify-between font-body text-xs">
                      <span className="text-foreground/50">Delivery · {d.supplier_name || 'Supplier'}</span>
                      <span className="text-foreground/70">R{money((d.amount ?? 0))}</span>
                    </div>
                  ))}
                  {(selectedOrder.delivery_total ?? 0) > 0 && (selectedOrder.delivery_lines || []).length === 0 && (
                    <div className="flex justify-between font-body text-sm">
                      <span className="text-foreground/60">Delivery fee</span>
                      <span className="text-foreground">R{money((selectedOrder.delivery_total ?? 0))}</span>
                    </div>
                  )}
                  {(selectedOrder.collection_fee ?? 0) > 0 && (
                    <div className="flex justify-between font-body text-sm">
                      <span className="text-foreground/60">Collection fee</span>
                      <span className="text-foreground">R{money((selectedOrder.collection_fee ?? 0))}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 mt-1 border-t border-border/60">
                    <span className="font-body text-sm font-semibold text-foreground">Total</span>
                    <span className="font-body text-base font-semibold text-vintage" data-testid="order-detail-total">R{money(selectedOrder.total)}</span>
                  </div>
                  {creditInfo(selectedOrder).hasCredit && (
                    <>
                      <div className="flex justify-between font-body text-sm" data-testid="order-detail-credit-applied">
                        <span className="text-gold/90">Account credit applied</span>
                        <span className="text-gold/90">− R{money(creditInfo(selectedOrder).creditApplied)}</span>
                      </div>
                      <div className="flex justify-between pt-2 mt-1 border-t border-border/60">
                        <span className="font-body text-sm font-semibold text-foreground">Amount payable (cash)</span>
                        <span className="font-body text-base font-semibold text-vintage" data-testid="order-detail-amount-payable">R{money(creditInfo(selectedOrder).amountPayable)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {/* Invoice — preview before download (Item #8) */}
              {['confirmed', 'packing', 'ready_for_collection', 'collection_notification_sent', 'closed'].includes(selectedOrder.status) && (
                <div className="grid grid-cols-2 gap-2" data-testid="dialog-invoice-actions">
                  <Button
                    onClick={() => previewInvoice(selectedOrder.id)}
                    disabled={previewingId === selectedOrder.id}
                    data-testid="dialog-preview-invoice-btn"
                    variant="outline"
                    className="rounded-full border-teal/40 text-teal hover:bg-teal/10 font-body text-sm gap-2"
                  >
                    {previewingId === selectedOrder.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                    Preview Invoice
                  </Button>
                  <Button
                    onClick={() => downloadInvoice(selectedOrder.id)}
                    data-testid="dialog-download-invoice-btn"
                    variant="outline"
                    className="rounded-full border-border hover:border-foreground/30 font-body text-sm gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                </div>
              )}

              {/* Manual WhatsApp send — any template, any stage */}
              <div className="border-t border-border pt-4" data-testid="order-send-whatsapp">
                <p className="font-body text-xs text-foreground/60 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <MessageCircle className="h-3.5 w-3.5 text-teal" /> Send a WhatsApp
                </p>
                {waTemplatesLoading ? (
                  <p className="font-body text-xs text-foreground/40"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" /> Loading templates…</p>
                ) : waTemplates.length === 0 ? (
                  <p className="font-body text-xs text-foreground/40">No WhatsApp templates available.</p>
                ) : (() => {
                  const chosen = waTemplates.find((t) => t.template === waTemplateChoice);
                  return (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select value={waTemplateChoice} onValueChange={setWaTemplateChoice}>
                          <SelectTrigger data-testid="send-wa-template-select" className="w-64 rounded-xl bg-card border-border font-body text-sm">
                            <SelectValue placeholder="Choose a message" />
                          </SelectTrigger>
                          <SelectContent>
                            {waTemplates.map((t) => (
                              <SelectItem key={t.template} value={t.template} disabled={!t.configured} data-testid={`send-wa-option-${t.template}`}>
                                {t.label}{t.recommended ? ' ★' : ''}{!t.configured ? ' (not set up)' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          data-testid="send-wa-btn"
                          onClick={sendWhatsAppTemplate}
                          disabled={waSending || !chosen || !chosen.configured || !waTemplatesTo}
                          size="sm"
                          className="rounded-full bg-teal text-background hover:bg-teal/90 font-body text-sm gap-1.5"
                        >
                          {waSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
                          Send WhatsApp
                        </Button>
                      </div>
                      {chosen && (
                        <div className="rounded-xl bg-muted border border-border p-3" data-testid="send-wa-preview">
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <p className="font-body text-[11px] uppercase tracking-wide text-foreground/50">Preview → {waTemplatesTo || 'no number on file'}</p>
                            {chosen.recommended
                              ? <span className="font-body text-[11px] text-teal">Fits this stage</span>
                              : <span className="font-body text-[11px] text-amber-300">Off-stage</span>}
                          </div>
                          <ul className="space-y-0.5">
                            {chosen.preview_lines.map((l, i) => (
                              <li key={i} className="font-body text-xs text-foreground/80">• {l}</li>
                            ))}
                          </ul>
                          {!chosen.configured && (
                            <p className="font-body text-[11px] text-red-400 mt-1.5">This template has no approved Meta name — it can't be sent yet.</p>
                          )}
                          {chosen.configured && !chosen.recommended && (
                            <p className="font-body text-[11px] text-amber-300 mt-1.5">{chosen.note}</p>
                          )}
                          {!waTemplatesTo && (
                            <p className="font-body text-[11px] text-red-400 mt-1.5">No contact cellphone on this order — add one above to send.</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>


              {(selectedOrder.notification_log || []).length > 0 && (
                <div className="border-t border-border pt-4" data-testid="order-notification-history">
                  <p className="font-body text-xs text-foreground/60 uppercase tracking-wide mb-2">Notification History</p>
                  <div className="space-y-1.5">
                    {[...(selectedOrder.notification_log || [])].reverse().map((n, i) => (
                      <div key={i} className="flex items-center gap-2 font-body text-xs" data-testid={`notif-log-${i}`}>
                        {n.channel === 'whatsapp'
                          ? <MessageCircle className="h-3.5 w-3.5 text-teal shrink-0" />
                          : <Mail className="h-3.5 w-3.5 text-gold shrink-0" />}
                        <span className="text-foreground/80 min-w-0 truncate">
                          {notifEventLabel(n.event)}
                          {n.status === 'resend' ? <span className="text-foreground/40"> · resend</span> : null}
                        </span>
                        <span className={`ml-auto shrink-0 font-semibold ${(() => {
                          if (n.read_at) return 'text-teal';
                          if (n.delivery_status === 'delivered' || n.delivered_at) return 'text-teal';
                          if (!n.success || n.delivery_status === 'failed') return 'text-red-400';
                          return 'text-foreground/70';
                        })()}`}>
                          {(() => {
                            const isWa = n.channel === 'whatsapp';
                            if (n.read_at) return isWa ? 'Read' : 'Opened';
                            if (n.delivery_status === 'delivered' || n.delivered_at) return 'Delivered';
                            if (!n.success || n.delivery_status === 'failed') return 'Failed';
                            return 'Sent';
                          })()}
                        </span>
                        <span className="text-foreground/40 shrink-0 w-28 text-right">
                          {n.at ? new Date(n.at).toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg', dateStyle: 'short', timeStyle: 'short' }) : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedOrder(null)} className="rounded-full border-border font-body text-sm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund item(s) modal */}
      <Dialog open={refundItems.length > 0} onOpenChange={(o) => { if (!o && !refundBusy) setRefundItems([]); }}>
        <DialogContent className="bg-background rounded-2xl max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              {refundItems.length > 1 ? `Refund ${refundItems.length} items` : 'Refund item'}
            </DialogTitle>
          </DialogHeader>
          {refundItems.length > 0 && (() => {
            const cardEligible = selectedOrder?.payment_method === 'card';
            return (
              <div className="space-y-4">
                <p className="font-body text-xs text-foreground/60 uppercase tracking-wide">You're refunding</p>
                <div className="rounded-xl bg-muted border border-border p-3">
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {refundItems.map((it, i) => {
                      const max = remainingQty(it);
                      const qty = refundQty[it.product_id] ?? max;
                      return (
                        <div key={i} className="flex items-center justify-between gap-3" data-testid={`refund-line-${i}`}>
                          <div className="min-w-0">
                            <p className="font-body text-sm text-foreground truncate">
                              {it.name} <span className="text-foreground/50">· R{money((it.price || 0))} ea</span>
                            </p>
                            <p className="font-body text-[11px] text-foreground/40">
                              {max} of {it.quantity} unit{it.quantity !== 1 ? 's' : ''} available to refund
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Input
                              type="number"
                              min={1}
                              max={max}
                              value={qty}
                              onChange={(e) => setRefundQtyFor(it, e.target.value)}
                              data-testid={`refund-qty-${i}`}
                              className="w-16 h-8 text-center rounded-lg bg-muted border-border font-body text-sm"
                            />
                            <span className="font-body text-sm text-foreground/70 w-16 text-right" data-testid={`refund-line-total-${i}`}>
                              R{money(((it.price || 0) * qty))}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                    <span className="font-body text-xs text-foreground/60 uppercase tracking-wide">
                      {refundItems.length > 1 ? 'Total refund' : 'Refund amount'}
                    </span>
                    <span className="font-heading text-lg text-gold" data-testid="refund-amount">R{money(refundTotal)}</span>
                  </div>
                </div>

                <p className="font-body text-xs text-foreground/60 uppercase tracking-wide">Choose how to refund</p>

                {/* Store credit option */}
                <button
                  type="button"
                  data-testid="refund-option-credit"
                  onClick={() => setRefundDest('credit')}
                  className={`w-full text-left rounded-xl border-2 p-3 transition-all ${refundDest === 'credit' ? 'border-gold bg-gold/15 ring-2 ring-gold/40 shadow-sm' : 'border-border hover:border-gold/40 hover:bg-gold/5'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${refundDest === 'credit' ? 'border-gold' : 'border-foreground/40'}`}>
                      {refundDest === 'credit' && <span className="h-2 w-2 rounded-full bg-gold" />}
                    </span>
                    <span className="font-body text-sm font-semibold text-foreground">Store credit <span className="text-foreground/50 font-normal">(instant)</span></span>
                    <Badge className="ml-auto bg-teal/15 text-teal border-teal/30 rounded-full font-body text-[10px]">Recommended</Badge>
                  </div>
                  <p className="font-body text-xs text-foreground/55 mt-1.5 pl-6">
                    R{money(refundTotal)} is added to the customer's account immediately — applied to their next order.
                  </p>
                </button>

                {/* Card option */}
                <button
                  type="button"
                  data-testid="refund-option-card"
                  disabled={!cardEligible}
                  onClick={() => cardEligible && setRefundDest('card')}
                  className={`w-full text-left rounded-xl border-2 p-3 transition-all ${!cardEligible ? 'border-border opacity-50 cursor-not-allowed' : refundDest === 'card' ? 'border-gold bg-gold/15 ring-2 ring-gold/40 shadow-sm' : 'border-border hover:border-gold/40 hover:bg-gold/5'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${refundDest === 'card' ? 'border-gold' : 'border-foreground/40'}`}>
                      {refundDest === 'card' && <span className="h-2 w-2 rounded-full bg-gold" />}
                    </span>
                    <span className="font-body text-sm font-semibold text-foreground">Refund to card via Paystack</span>
                  </div>
                  <p className="font-body text-xs text-foreground/55 mt-1.5 pl-6">
                    Returns R{money(refundTotal)} to the original card.
                  </p>
                  {!cardEligible && (
                    <p className="font-body text-xs text-red-400 mt-1 pl-6" data-testid="refund-card-unavailable">
                      ⚠ Not available — this order wasn't paid by card.
                    </p>
                  )}
                </button>

                <div>
                  <label className="font-body text-xs text-foreground/70">Note (optional)</label>
                  <Input
                    data-testid="refund-note-input"
                    value={refundNote}
                    onChange={(e) => setRefundNote(e.target.value)}
                    placeholder="e.g. supplier out of stock"
                    className="mt-1 rounded-lg bg-muted border-border font-body text-sm"
                  />
                </div>

                <div className="flex items-start gap-2 rounded-lg bg-red-900/15 border border-red-700/30 p-2.5">
                  <span className="text-red-400 text-sm leading-none mt-0.5">⚠</span>
                  <p className="font-body text-xs text-red-400/90">
                    {refundItems.every((it) => (refundQty[it.product_id] ?? remainingQty(it)) >= remainingQty(it))
                      ? `This refunds all remaining units and marks ${refundItems.length > 1 ? 'these items' : 'the item'} as out of stock. This cannot be undone.`
                      : `This partial refund removes the selected units from the order total; the remaining units stay live. This cannot be undone.`}
                  </p>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRefundItems([])}
              disabled={refundBusy}
              data-testid="refund-cancel-btn"
              className="rounded-full border-border font-body text-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmRefund}
              disabled={refundBusy}
              data-testid="refund-confirm-btn"
              className="rounded-full bg-red-600 hover:bg-red-600/90 text-white font-body text-sm font-semibold gap-2"
            >
              {refundBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Refund R${money(refundTotal)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
